"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Button, Slider } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import {
  Captions,
  CaptionsOff,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useInView } from "motion/react";

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * The walkthrough player. Unlike the landing trailer this one carries a
 * voiceover, so it waits for a click, starts with sound, and needs the controls
 * a talking video is watched with: captions and a bar you can drag.
 */
export const ExplainerPlayer = ({
  src,
  poster,
  captionsSrc,
}: {
  src: string;
  poster: string;
  captionsSrc: string;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(videoRef, { margin: "-100px 0px -100px 0px" });
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [cue, setCue] = useState("");
  const [pointerInside, setPointerInside] = useState(false);
  const [tapRevealed, setTapRevealed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const play = () => {
    const video = videoRef.current;
    if (!video) return;

    // Unmuting is only allowed from a user gesture, which is exactly what this is.
    video.muted = false;
    setHasStarted(true);
    video.play();
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    // "ended" does not imply "pause", so the poster needs its own trigger.
    const onEnded = () => {
      setIsPlaying(false);
      setHasStarted(false);
    };
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDuration = () => setDuration(video.duration);
    const onVolumeChange = () => setMuted(video.muted);

    // Metadata can land before this effect runs, and then the events above never
    // fire again — without this the seek bar would sit at max 0, i.e. dead.
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) onDuration();
    onVolumeChange();

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onDuration);
    video.addEventListener("durationchange", onDuration);
    video.addEventListener("volumechange", onVolumeChange);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onDuration);
      video.removeEventListener("durationchange", onDuration);
      video.removeEventListener("volumechange", onVolumeChange);
    };
  }, []);

  // The cues are rendered by hand: "hidden" keeps them firing while stopping the
  // browser from painting its own caption box, which we cannot style to match.
  useEffect(() => {
    const track = videoRef.current?.textTracks[0];
    if (!track) return;

    track.mode = "hidden";
    const onCueChange = () => {
      const active = track.activeCues?.[track.activeCues.length - 1];
      setCue(active && "text" in active ? String(active.text) : "");
    };

    track.addEventListener("cuechange", onCueChange);
    return () => track.removeEventListener("cuechange", onCueChange);
  }, []);

  // Nobody wants a voiceover playing from a section they scrolled past. Coming
  // back does not resume: by then it is the visitor's call, not ours.
  useEffect(() => {
    if (!isInView) videoRef.current?.pause();
  }, [isInView]);

  /**
   * Out of the way while watching, but a paused video keeps its bar: with the
   * poster gone there would otherwise be nothing to aim at. A touch pointer is
   * destroyed the moment the finger lifts, which fires pointerleave — so a
   * phone cannot hover, and taps on the frame toggle the bar instead.
   */
  const controlsVisible =
    hasStarted && (pointerInside || tapRevealed || !isPlaying);

  const seek = (value: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <div
      className="relative w-full h-full"
      onPointerEnter={(event) =>
        event.pointerType === "mouse" && setPointerInside(true)
      }
      onPointerLeave={(event) =>
        event.pointerType === "mouse" && setPointerInside(false)
      }
    >
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full absolute top-0 left-0 object-cover"
        onPointerUp={(event) =>
          event.pointerType !== "mouse" && setTapRevealed((shown) => !shown)
        }
        controls={false}
        preload="metadata"
        playsInline
      >
        <track
          kind="captions"
          src={captionsSrc}
          srcLang="en"
          label="English"
          default
        />
      </video>

      {hasStarted && (
        <>
          {captionsOn && cue && (
            <div
              className={cn(
                // Decorative text: on a narrow frame it covers the middle of the
                // video, and a tap there has to reach the tap-to-reveal handler.
                "pointer-events-none absolute left-0 z-20 flex w-full justify-center px-4 transition-[bottom] duration-200 lg:px-5",
                controlsVisible
                  ? "bottom-[60px] lg:bottom-[86px]"
                  : "bottom-4 lg:bottom-6",
              )}
            >
              <p className="max-w-[92%] rounded-[12px] bg-[rgba(37,25,3,.82)] px-3 py-1.5 text-center text-[13px] font-semibold leading-[18px] text-white lg:max-w-[80%] lg:px-3.5 lg:py-2 lg:text-lg lg:leading-7">
                {cue}
              </p>
            </div>
          )}

          <div
            className={cn(
              "absolute bottom-0 left-0 z-20 flex w-full items-center gap-2 bg-[rgba(37,25,3,.72)] px-3 py-2.5 backdrop-blur-sm transition-[opacity,translate] duration-200 lg:gap-4 lg:px-5 lg:py-3.5",
              controlsVisible
                ? "translate-y-0 opacity-100"
                : "pointer-events-none translate-y-full opacity-0",
            )}
          >
            <Button
              onClick={() => (isPlaying ? videoRef.current?.pause() : play())}
              variant="d-primary"
              aria-label={isPlaying ? "Pause" : "Play"}
              className="size-8 shrink-0 border-none [&_svg]:size-4 lg:size-9"
            >
              {isPlaying ? <Pause fill="black" /> : <Play fill="black" />}
            </Button>

            <span className="hidden shrink-0 text-[13px] font-semibold leading-4 text-white tabular-nums sm:inline-block lg:text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <Slider
              value={[Math.min(currentTime, duration || 0)]}
              max={duration || 0}
              step={0.1}
              aria-label="Seek"
              onValueChange={([value]) => seek(value ?? 0)}
              className={cn(
                "flex-1 cursor-pointer",
                "[&_[data-slot=slider-track]]:h-1.5 [&_[data-slot=slider-track]]:bg-white/30",
                "[&_[data-slot=slider-range]]:bg-[#FFD602]",
                "[&_[data-slot=slider-thumb]]:size-3.5 [&_[data-slot=slider-thumb]]:border-none [&_[data-slot=slider-thumb]]:bg-[#FFD602]",
              )}
            />

            <Button
              onClick={() => {
                const video = videoRef.current;
                if (video) video.muted = !video.muted;
              }}
              variant="d-primary"
              aria-label={muted ? "Unmute" : "Mute"}
              className="size-8 shrink-0 border-none [&_svg]:size-4 lg:size-9"
            >
              {muted ? <VolumeX fill="black" /> : <Volume2 fill="black" />}
            </Button>

            <Button
              onClick={() => setCaptionsOn((on) => !on)}
              variant="d-primary"
              aria-label={captionsOn ? "Hide captions" : "Show captions"}
              aria-pressed={captionsOn}
              className={cn(
                "size-8 shrink-0 border-none [&_svg]:size-4 lg:size-9",
                !captionsOn && "opacity-60",
              )}
            >
              {captionsOn ? <Captions /> : <CaptionsOff />}
            </Button>
          </div>
        </>
      )}

      {!hasStarted && (
        <>
          <Image
            src={poster}
            alt=""
            width={1100}
            height={780}
            className="object-cover absolute top-0 left-0 w-full h-full"
          />
          <div className="absolute flex items-center justify-center w-full h-full bg-[rgba(37,25,3,.56)] top-0 left-0">
            <Button
              onClick={play}
              variant="d-primary"
              aria-label="Play"
              className="border-none size-[50px]"
            >
              <Play size={24} fill="black" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
